import { Input, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';

import {
    padMilitaryTime,
    parseMilitaryTime,
    validateMilitaryTime,
} from '@clh/util';

type MilitaryTimeInputProps = {
    label: string;
    // Source-of-truth dayjs whose time-of-day this input edits. Only the
    // hour/minute are surfaced; the date is preserved by the caller via
    // applyTimeOfDay when the emitted time is merged back in.
    value: dayjs.Dayjs;
    // Emits a dayjs carrying the newly entered time-of-day once the input holds
    // a valid four-digit military time. The caller maps it onto the correct date.
    onTimeChange: (time: dayjs.Dayjs) => void;
    disabled?: boolean;
    // External error styling (e.g. on-call window / segment validation) that is
    // driven by the parent independently of the HHMM format check.
    hasExternalError?: boolean;
};

// Plain four-digit military time input for the admin Assignment tab, mirroring
// the webapp MilitaryTimeInput's HHMM behavior without react-hook-form so it can
// bind to the log-time form's useState-driven dayjs state.
const MilitaryTimeInput = ({
    label,
    value,
    onTimeChange,
    disabled,
    hasExternalError,
}: MilitaryTimeInputProps) => {
    const [text, setText] = useState(() => value.format('HHmm'));

    const formatError = validateMilitaryTime(text);
    const showError = !disabled && !!formatError;

    return (
        <div style={{ display: 'inline-flex', flexDirection: 'column' }}>
            <label className="form-label" style={{ fontWeight: 500 }}>
                {label}
            </label>
            <Input
                inputMode="numeric"
                placeholder="HHMM"
                disabled={disabled}
                status={showError || hasExternalError ? 'error' : ''}
                style={{ width: 96, textAlign: 'center' }}
                value={text}
                onChange={(e) => {
                    const digits = e.target.value
                        .replace(/\D/g, '')
                        .slice(0, 4);
                    setText(digits);
                    const parsed = parseMilitaryTime(digits);
                    if (parsed) {
                        onTimeChange(
                            dayjs().hour(parsed.hour).minute(parsed.minute)
                        );
                    }
                }}
                onBlur={() => {
                    const padded = padMilitaryTime(text);
                    setText(padded);
                    const parsed = parseMilitaryTime(padded);
                    if (parsed) {
                        onTimeChange(
                            dayjs().hour(parsed.hour).minute(parsed.minute)
                        );
                    }
                }}
            />
            {showError && (
                <Typography.Text type="danger" style={{ fontSize: 12 }}>
                    {formatError}
                </Typography.Text>
            )}
        </div>
    );
};

export default MilitaryTimeInput;
